// Material ui
import { Button, Checkbox, Fab, List, ListItemButton, ListItemIcon, ListItemText, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import MaterialDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import FormatListIcon from '@mui/icons-material/FormatListBulleted';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { styled, SxProps, Theme } from '@mui/material/styles';

// Repeated 2x: centered flex Typography with top/bottom margin (dialog/card title)
const CenteredDialogTitle = styled(Typography)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 8, // mt: 1
  marginBottom: 16, // mb: 2
  textAlign: 'center',
});

type OnboardingProps = {
  title: string;
  steps: Array<OnboardingStep>;
  asDialog: boolean;
  setAsDialog: (asDialog: boolean) => void;
  stopOnboarding: () => void;
  setNavigateNow: (navigateNow: boolean) => void;
  setUrl: (url: string) => void;
};

type OnboardingStep = {
  text: string;
  checked: boolean;
  redirect?: string;
  clickRedirect?: string;
  autoRedirect?: boolean;
};

const Onboarding = ({ steps, title, asDialog, setAsDialog, stopOnboarding, setNavigateNow, setUrl }: OnboardingProps) => {
  const { t } = useTranslation('events');
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.down(1700));
  const done = steps.every((step) => step.checked);

  return (
    <>
      {!asDialog && (
        <Box
          sx={{
            display: 'flex',
            position: 'fixed',
            [theme.breakpoints.up(1700)]: {
              right: 2,
              top: '35%',
            },
          }}>
          {small ? (
            <>
              <Fab
                color='secondary'
                onClick={() => setAsDialog(true)}
                sx={{ position: 'fixed', bottom: 10, right: 2, [theme.breakpoints.up(1000)]: { bottom: 2 }, zIndex: 1 }}
                variant='extended'>
                <FormatListIcon sx={{ mr: 1 }} />
                {t('ONBOARDING.HELP')}
              </Fab>
            </>
          ) : (
            <Paper
              elevation={15}
              onClick={() => setAsDialog(true)}
              sx={{ display: 'flex', flexDirection: 'column', width: '250px', position: 'relative', cursor: 'pointer' }}>
              <Box sx={{ p: 1 }}>
                <OnboardingBody disabled={true} setNavigateNow={setNavigateNow} setUrl={setUrl} steps={steps} title={title} />
              </Box>
            </Paper>
          )}
        </Box>
      )}
      <MaterialDialog
        aria-labelledby='form-dialog-title'
        fullWidth
        maxWidth={'sm'}
        onClose={() => (done ? stopOnboarding() : setAsDialog(false))}
        open={Boolean(asDialog)}>
        <Box sx={{ p: 1 }}>
          <OnboardingBody disabled={false} setNavigateNow={setNavigateNow} setUrl={setUrl} steps={steps} title={title} />
        </Box>

        {done ? (
          <CenteredDialogTitle variant='h5'>{t('ONBOARDING.DONE')}</CenteredDialogTitle>
        ) : (
          <Box sx={{ color: 'red', display: 'flex', pt: 1, justifyContent: 'center' }}>
            <Button color='inherit' onClick={() => stopOnboarding()} variant='outlined'>
              {t('ONBOARDING.NOHELP')}
            </Button>
          </Box>
        )}

        <DialogActions>
          <Box onClick={() => (done ? stopOnboarding() : setAsDialog(false))} sx={{ cursor: 'pointer' }}>
            <CloseIcon />
          </Box>
        </DialogActions>
      </MaterialDialog>
    </>
  );
};

export default Onboarding;

type OnboardingBodyProps = {
  steps: Array<OnboardingStep>;
  title: string;
  setNavigateNow: (navigateNow: boolean) => void;
  setUrl: (url: string) => void;
  disabled?: boolean;
};

const OnboardingBody = ({ steps, title, setNavigateNow, setUrl, disabled = false }: OnboardingBodyProps) => {
  const handleStepClick = (step: OnboardingStep) => {
    if (step.redirect && !disabled) {
      setUrl(step.redirect);
      setNavigateNow(true);

      if (step.clickRedirect) {
        setTimeout(() => {
          setUrl(step.clickRedirect || '');
          setNavigateNow(true);
        }, 300);
      }
    }
  };

  const RegisteredCheckboxIcon = () => {
    return (
      <svg
        style={{
          fill: '#14ad14',
          width: '1em',
          height: '1em',
          display: 'inline-block',
          fontSize: '1.5rem',
          transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          flexShrink: 0,
          userSelect: 'none',
        }}>
        <path d='M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' />
      </svg>
    );
  };

  const isNextStep = (step: OnboardingStep) => {
    return steps.find((step) => !step.checked) === step;
  };

  const isFutureStep = (step: OnboardingStep) => {
    return steps.findIndex((step) => !step.checked) < steps.findIndex((s) => s === step) && steps.some((step) => !step.checked);
  };

  const isPastStep = (step: OnboardingStep) => {
    return steps.findIndex((step) => !step.checked) > steps.findIndex((s) => s === step) || !steps.some((step) => !step.checked);
  };

  return (
    <>
      <CenteredDialogTitle variant='h3'>{title}</CenteredDialogTitle>
      <List>
        {steps.map((step, index) => {
          const next = isNextStep(step);
          const past = isPastStep(step);
          const primarySx: SxProps<Theme> = past
            ? { color: 'green', wordBreak: 'break-word' }
            : next
              ? { wordBreak: 'break-word', fontWeight: 'bold' }
              : { wordBreak: 'break-word' };
          return (
            <ListItemButton
              dense
              disabled={isFutureStep(step)}
              key={index}
              onClick={() => handleStepClick(step)}
              role={undefined}
              sx={next ? { boxShadow: '0px 2px 8px 0px rgba(81, 203, 238, 1)' } : undefined}>
              <ListItemText secondary={false} slotProps={{ primary: { sx: primarySx } }}>
                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                  <Box sx={{ whiteSpace: 'nowrap', mr: 0.5 }}>{`${index + 1}.`}</Box>
                  {`${step.text}`}
                </Box>
              </ListItemText>
              <ListItemIcon>
                <Checkbox
                  disableRipple
                  disabled={true}
                  edge='start'
                  icon={step.checked ? <RegisteredCheckboxIcon /> : undefined}
                  sx={{ ml: 1 }}
                  tabIndex={-1}
                />
              </ListItemIcon>
            </ListItemButton>
          );
        })}
      </List>
    </>
  );
};
